import React, { useEffect, useState } from 'react';
import { navigate } from 'gatsby';
import './src/styles/global.css';
import './scripts/newRelic.min';
import ErrorModal from './src/components/modals/ErrorModal/ErrorModal';
import LoadingOverlay from './src/components/LoadingOverlay/LoadingOverlay';
import ChangeTargetMsisdn from './src/components/modals/ChangeTargetMsisdn/ChangeTargetMsisdn';
import CheckoutSDKListener from './src/components/CheckoutSDKListener/CheckoutSDKListener';
import ErrorBoundary from './src/components/ErrorBoundary/ErrorBoundary';
import StageTwoGate from './src/components/StageTwoGate/StageTwoGate';
import { useSessionStore } from './src/store/session';
import { useCheckoutStore } from './src/store/checkout';
import { useMsisdnChangeStore } from './src/store/msisdnChange';

// Força refetch dos manifests /page-data/*.json (app-data.json,
// page-data.json) — sem isso, browser de usuário que veio da v1 (com
// gatsby-plugin-offline + cache HTTP de longa duração) serve manifest
// antigo, causando hash mismatch no navigation.js do Gatsby 5 e
// disparando window.location = ... (hard reload em loop).
//
// Dois patches necessários porque o Gatsby usa fetch em alguns lugares
// e XMLHttpRequest em outros (loader.js usa XHR).
export const onClientEntry = () => {
  // Helper de teste E2E: expõe a store de checkout para os specs Cypress
  // conseguirem simular cenários de vazamento de estado (ex.: sdkSessionActive
  //=true) sem precisar reproduzir o fluxo Flutter nativo inteiro. Só roda
  // dentro do runner do Cypress (window.Cypress é undefined em produção).
  if (window.Cypress) {
    window.useCheckoutStore = useCheckoutStore;
    window.useMsisdnChangeStore = useMsisdnChangeStore;
  }
  const PAGE_DATA_PATTERN = /\/page-data\/.+\.json(\?|$)/;

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const url = typeof input === 'string' ? input : input?.url;
    if (url && PAGE_DATA_PATTERN.test(url)) {
      return originalFetch(input, { ...(init || {}), cache: 'reload' });
    }
    return originalFetch(input, init);
  };

  const originalOpen = window.XMLHttpRequest.prototype.open;
  window.XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    let finalUrl = url;
    if (typeof url === 'string' && PAGE_DATA_PATTERN.test(url)) {
      const sep = url.includes('?') ? '&' : '?';
      finalUrl = `${url}${sep}_cb=${Date.now()}`;
    }
    return originalOpen.call(this, method, finalUrl, ...rest);
  };
};

const PUBLIC_PATHS = [
  '/web/login',
  '/web/sms',
  // Cliente Beta bloqueado não tem sessão: a tela precisa abrir sem msisdn.
  '/web/canal-exclusivo',
  '/login/',
  '/api-payment/',
  '/404',
];

const isPublicPath = path => PUBLIC_PATHS.some(p => path.startsWith(p));

function AuthGuard({ element, path }) {
  const msisdn = useSessionStore(state => state.msisdn);
  const [mounted, setMounted] = useState(false);
  const isPublic = isPublicPath(path);
  const isProtectedWithoutSession = !isPublic && !msisdn;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && isProtectedWithoutSession) {
      navigate('/web/login');
    }
  }, [mounted, isProtectedWithoutSession]);

  // Antes do mount: sempre renderiza o elemento para coincidir com o SSR
  if (mounted && isProtectedWithoutSession) return null;

  return element;
}

export const wrapRootElement = ({ element }) => (
  <>
    {element}
    <ErrorModal />
    <LoadingOverlay />
    <StageTwoGate>
      <ChangeTargetMsisdn />
      <CheckoutSDKListener />
    </StageTwoGate>
  </>
);

export const wrapPageElement = ({ element, props }) => (
  <ErrorBoundary key={props.location.pathname}>
    <AuthGuard element={element} path={props.location.pathname} />
  </ErrorBoundary>
);
